import {
    blogs_api_get_group_feed_url,
    blogs_api_group_create_post_url,
    blogs_api_remove_group_cover_photo_url,
    blogs_api_update_group_cover_photo_url,
} from 'mk/urls/functions';
import { groupPostCreateFormName } from 'mk2/apps/blogs/components/BlogPostCreateForm';
import { BoxedHashtags } from 'mk2/apps/blogs/components/HashtagsBox';
import {
    BlogPostEntity,
    BlogPostSchema,
    GroupEntity,
    GroupMemberEntity,
    GroupMemberSchema,
    GroupSchema,
} from 'mk2/apps/blogs/schemas';
import { groupsListUrl } from 'mk2/apps/blogs/urls';
import { parsePageNumber } from 'mk2/apps/forum/utils';
import { GROUP_COVER_UPLOAD_FORM_NAME } from 'mk2/apps/groups/components/GroupCoverForm';
import { groupsGroupUrl } from 'mk2/apps/groups/urls';
import { FormPageType } from 'mk2/constants';
import { ComplaintEntity, ComplaintSchema } from 'mk2/containers/Complaint/Complaint.schemas';
import { photosUploadTrigger } from 'mk2/containers/PhotosUpload/PhotosUpload.actions';
import { toolbarCloseAll } from 'mk2/containers/Toolbar/Toolbar.actions';
import {
    handleXHRGetErrorSaga,
    handleXHRPostErrorSaga,
    normalizeError,
    XHRAction,
    XHRFormError,
    XHRGetError,
    XHRPostError,
} from 'mk2/helpers/api';
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from 'mk2/helpers/form';
import { mkReduxFormOpenPage } from 'mk2/helpers/form.actions';
import { PhotoUploadStatus } from 'mk2/helpers/form.reducers';
import { getPhotoPreview } from 'mk2/helpers/photos';
import { scrollElementTo } from 'mk2/helpers/scrolling';
import { tupdate } from 'mk2/helpers/types';
import { removeEmpty, removePage1, url } from 'mk2/helpers/urls';
import { getLogger } from 'mk2/logger';
import { PhotoPreviewSchema, UserEntity } from 'mk2/schemas';
import { getRoutingLocation } from 'mk2/selectors';
import { normalize } from 'normalizr';
import { parse } from 'query-string';
import React from 'react';
import { replace } from 'react-router-redux';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { isNumber } from 'util';
import {
    GROUP_COVER_REMOVE_TRIGGER,
    GROUP_COVER_SUBMIT_TRIGGER,
    GROUP_COVER_UPLOAD_TRIGGER,
    GROUP_FEED_FETCH_TRIGGER,
    GROUP_FEED_SCROLLTO_TRIGGER,
    GROUP_POST_CREATE_TRIGGER,
    groupCoverRemoveApi,
    GroupCoverRemoveNormalizedResponse,
    GroupCoverRemoveTriggerAction,
    groupCoverSubmitApi,
    GroupCoverSubmitNormalizedResponse,
    GroupCoverSubmitTriggerAction,
    groupCoverUploadApi,
    GroupCoverUploadNormalizedResponse,
    GroupCoverUploadTriggerAction,
    groupFeedFetchApi,
    GroupFeedFetchNormalizedResponse,
    GroupFeedFetchTriggerAction,
    groupFeedScrollToTrigger,
    GroupFeedScrollToTriggerAction,
    groupPostCreateApi,
    GroupPostCreateNormalizedResponse,
    GroupPostCreateTriggerAction,
} from './GroupFeed.actions';
import { ScrollToPosition } from './GroupFeedPage';

const logger = getLogger('groups/GroupFeed.sagas');

interface ApiResponseGroupFeed {
    body: {
        group: GroupEntity;
        moderators: GroupMemberEntity[];
        posts: BlogPostEntity[];
        complaints: ComplaintEntity[];
        count: number;
        page: number;
        numberOfPages: number;
        deletedBy: UserEntity;
        memberships: number[];
        askedToJoin: number[],
        favorites: number[],
        isBlocked: boolean;
        isSubscribed: boolean;
        hasComplaints: boolean;
        hashtags: BoxedHashtags;
    };
}

function* fetchGroupFeed({ currentUrl, groupId, numPostsBeforeLoad, pageRef, orderBy, query, xhr }: GroupFeedFetchTriggerAction & XHRAction) {
    const qs = parse(currentUrl.search);

    if (!pageRef) {
        // if pageRef is 0, negative or invalid string, we redirect us to page=1
        const gotoUrl = url(groupsGroupUrl, { groupId }, removeEmpty(removePage1({ ...qs, page: 1 })));
        yield put(replace(gotoUrl));
        return;
    } else if (pageRef && qs.page && qs.page !== pageRef.toString()) {
        // if we succeed parsing incorrect string (e.g. page=3a), then redir to correct page (e.g. page=3)
        const gotoUrl = url(groupsGroupUrl, { groupId }, removeEmpty(removePage1({ ...qs, page: pageRef })));
        yield put(replace(gotoUrl));
        return;
    }

    if (numPostsBeforeLoad > 0 && qs.scrollto) {
        // if data is already loaded, scroll to bottom immediately
        // so that user does not experience scroll delayed by api call
        yield put(groupFeedScrollToTrigger(qs.scrollto));
    }

    yield put(groupFeedFetchApi.request(groupId, pageRef, orderBy, query));

    try {
        const response: ApiResponseGroupFeed = yield call(
            () => xhr.get(blogs_api_get_group_feed_url(groupId), { orderby: orderBy, page: pageRef, query }),
        );

        // ak skusame nahravat zly page number (negativny, vacsi ako pocet stranok, atd.), backend nas redirectne
        if (isNumber(pageRef) && pageRef !== response.body.page) {
            throw new Error('Wrong page');
        }

        const normalizedResponse: GroupFeedFetchNormalizedResponse = normalize({
            complaints: response.body.complaints,
            group: response.body.group,
            posts: response.body.posts,
            moderators: response.body.moderators,
        }, {
            complaints: [ComplaintSchema],
            group: GroupSchema,
            posts: [BlogPostSchema],
            moderators: [GroupMemberSchema],
        });

        yield put(groupFeedFetchApi.success(
            groupId,
            pageRef,
            orderBy,
            query,
            response.body.page,
            response.body.numberOfPages,
            response.body.count,
            normalizedResponse,
            response.body.memberships,
            response.body.askedToJoin,
            response.body.favorites,
            response.body.isBlocked,
            response.body.isSubscribed,
            response.body.hasComplaints,
            response.body.hashtags,
        ));

        if (pageRef === 'last') {
            // replace page='last' with real page index
            const gotoUrl = url(groupsGroupUrl, { groupId }, removeEmpty(removePage1({ ...qs, page: response.body.page })));
            yield put(replace(gotoUrl));
        } else if (qs.page === '1') {
            // remove ?page=1 from url
            const gotoUrl = url(groupsGroupUrl, { groupId }, removeEmpty(removePage1({ ...qs, page: 1 })));
            yield put(replace(gotoUrl));
        }

        const numPostsAfterLoad = response.body.posts ? response.body.posts.length : 0;
        if (numPostsBeforeLoad !== numPostsAfterLoad && qs.scrollto) {
            // other count of posts, either first load, or data on server changed, so scroll again
            yield put(groupFeedScrollToTrigger(qs.scrollto));
        }

    } catch (error) {
        if (error.status === 403 && error.response.body && error.response.body.wasMember) {
            yield put(replace(url(groupsListUrl, { type: 'public' }, { notMember: 'true' })));
        } else {
            yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        }
        yield put(groupFeedFetchApi.failure(groupId, pageRef, orderBy, query, normalizeError(error)));
    }
}

function* scrolltoGroupFeed({ scrollto }: GroupFeedScrollToTriggerAction) {
    if (scrollto === ScrollToPosition.LastPost) {
        scrollElementTo('#scrolltoLastHere', 'window-bottom', 0);
    } else if (scrollto === ScrollToPosition.FirstPost) {
        scrollElementTo('#scrolltoFirstHere', 'window-top', 40);
    }
}

function* uploadCoverPhoto({ groupId, config, files, xhr }: GroupCoverUploadTriggerAction & XHRAction) {
    try {
        yield put(mkReduxFormOpenPage(GROUP_COVER_UPLOAD_FORM_NAME, FormPageType.ChangeGroupCover));
        yield put(groupCoverUploadApi.request(groupId, config, files));
        yield put(photosUploadTrigger(GROUP_COVER_UPLOAD_FORM_NAME, config, files, FormPageType.ChangeGroupCover));
        const photoPreview = [];
        for (let i = 0; i < files.length; i++) {
            const onePhotoPreview = yield call(getPhotoPreview, files[i]);
            photoPreview.push(onePhotoPreview);
        }
        const normalizedResponse: GroupCoverUploadNormalizedResponse = normalize(
            {photoPreview},
            {photoPreview: [PhotoPreviewSchema]},
        );
        yield put(groupCoverUploadApi.success(groupId, config, files, normalizedResponse));
    } catch (error) {
        yield put(groupCoverUploadApi.failure(groupId, config, files, error));
    }
}

interface ApiResponseRemoveCoverPhoto {
    body: {
        group: GroupEntity;
    };
}

function* removeCoverPhoto({ groupId, xhr }: GroupCoverRemoveTriggerAction & XHRAction) {
    yield put(groupCoverRemoveApi.request(groupId));
    try {
        const response: ApiResponseRemoveCoverPhoto = yield call(() => xhr.post(blogs_api_remove_group_cover_photo_url(), { groupId }));
        const normalizedResponse: GroupCoverRemoveNormalizedResponse = normalize(response.body, {
            group: GroupSchema,
        });
        yield put(groupCoverRemoveApi.success(groupId, normalizedResponse));
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        yield put(groupCoverRemoveApi.failure(groupId, normalizeError(error)));
    }
}

interface ApiResponseSaveCoverPhoto {
    body: {
        group: GroupEntity;
    };
}

function* saveCoverPhoto({values, groupId, xhr}: GroupCoverSubmitTriggerAction & XHRAction) {
    yield put(groupCoverSubmitApi.request(values, groupId));

    if (values.photos.length !== 1 || !values.photos[0]) {
        throw new Error('Missing photo');
    }

    try {
        const response: ApiResponseSaveCoverPhoto = yield call(
            () => xhr.put(blogs_api_update_group_cover_photo_url(), {
                groupId,
                photoId: values.photos[0].photoId,
            }),
        );
        const normalizedResponse: GroupCoverSubmitNormalizedResponse = normalize(response.body, {group: GroupSchema});
        yield handleXHRFormSuccessSaga(GROUP_COVER_UPLOAD_FORM_NAME);
        yield put(groupCoverSubmitApi.success(values, groupId, normalizedResponse));
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(groupCoverSubmitApi.failure(values, groupId, normalizeError(error)));
    }
}

export interface ApiResponsePostCreate {
    body: {
        post: BlogPostEntity;
    };
}

function* postCreate({ groupId, pageRef, postFd, xhr }: GroupPostCreateTriggerAction & XHRAction) {
    const location = yield select(getRoutingLocation);
    const query = parse(location.search);
    const searchQuery = query.query;
    const currentPage = parsePageNumber(location);

    const formName = groupPostCreateFormName(groupId, pageRef);

    yield put(groupPostCreateApi.request(groupId, postFd));
    try {
        const response: ApiResponsePostCreate = yield call(
            () => xhr.post(blogs_api_group_create_post_url(groupId), {
                postData: tupdate(postFd, {
                    photos: postFd.photos ? postFd.photos.filter((photo) => photo.status === PhotoUploadStatus.SUCCESS) : [],
                }),
            }),
        );

        const normalizedResponse: GroupPostCreateNormalizedResponse = normalize(response.body, {
            post: BlogPostSchema,
        });

        // close toolbar, e.g. photos might be opened
        yield put(toolbarCloseAll(formName));

        const nextPage = 1;
        const gotoUrl = (nextPage !== currentPage || searchQuery !== undefined)
            ? url(groupsGroupUrl, { groupId }, removePage1(tupdate(query, {page: nextPage, query: undefined})))
            // stay on the same url
            : null;

        yield handleXHRFormSuccessSaga(formName, gotoUrl);
        yield put(groupPostCreateApi.success(groupId, postFd, normalizedResponse));

    } catch (error) {
        yield handleXHRFormErrorSaga(formName, error as XHRFormError, logger);
        yield put(groupPostCreateApi.failure(groupId, postFd, normalizeError(error)));
    }
}

export default function* root() {
    yield all([
        // musi byt takeEvery pre GROUP_FEED_FETCH_TRIGGER a nie takeLatest,
        // aby sa docitali vsetky stranky, cez ktore rychlo pageujem. Inak sa killne
        // redux saga pocas jej vykonavania a loadingState ostane v stave LOADING.
        // Ked sa na nu znova vratim, uz sa nezacne znova nacitavat, lebo nie je v stave INIT
        takeEvery(GROUP_FEED_FETCH_TRIGGER, fetchGroupFeed),
        takeLatest(GROUP_FEED_SCROLLTO_TRIGGER, scrolltoGroupFeed),
        takeLatest(GROUP_COVER_UPLOAD_TRIGGER, uploadCoverPhoto),
        takeLatest(GROUP_COVER_REMOVE_TRIGGER, removeCoverPhoto),
        takeLatest(GROUP_COVER_SUBMIT_TRIGGER, saveCoverPhoto),
        takeEvery(GROUP_POST_CREATE_TRIGGER, postCreate),
    ]);
}
